import React from 'react';
import { CheckCircle2, XCircle, Shield } from 'lucide-react';
import { insuranceData } from '../data/mock';

// Handles the two-layer logo fallback (Wikipedia → Google Favicon → text-only)
// without pulling extra React state. If both image URLs fail, the wrapper
// switches to `.no-logo` and renders the insurer name as a large label.
const InsurerLogo = ({ insurer }) => {
  const handleError = (e) => {
    const img = e.currentTarget;
    if (insurer.logoFallback && img.src !== insurer.logoFallback) {
      img.src = insurer.logoFallback;
      return;
    }
    // both sources failed — swap to text mode
    const wrapper = img.closest('.insurance-logo-wrap');
    if (wrapper) wrapper.classList.add('is-text-fallback');
    img.style.display = 'none';
  };

  return (
    <div className="insurance-logo-wrap">
      {insurer.logo ? (
        <img
          src={insurer.logo}
          alt={`${insurer.name} logo`}
          className="insurance-logo-img"
          width="180"
          height="90"
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={handleError}
        />
      ) : null}
      <span className="insurance-logo-text">{insurer.name}</span>
    </div>
  );
};

// The accepted / not-accepted grids — shared between the homepage section
// and the Fees page "Insurance & funding" section (styles live in App.css).
export const InsuranceGrids = () => {
  const accepted = insuranceData.filter((i) => i.accepted);
  const notAccepted = insuranceData.filter((i) => !i.accepted);

  return (
    <>
      {accepted.length > 0 && (
        <>
          <h3 className="insurance-group-title">
            <CheckCircle2 size={18} className="insurance-title-icon insurance-title-icon-accepted" />
            Accepted providers
          </h3>
          <div className="insurance-grid insurance-grid-accepted" data-testid="insurance-accepted-grid">
            {accepted.map((insurance) => (
              <div
                key={`accepted-${insurance.name}`}
                className="insurance-card insurance-card-accepted"
                data-testid={`insurance-accepted-${insurance.name}`}
              >
                <CheckCircle2 className="insurance-check" size={20} />
                <InsurerLogo insurer={insurance} />
                <span className="insurance-name">{insurance.name}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {notAccepted.length > 0 && (
        <>
          <h3 className="insurance-group-title insurance-group-title-excluded">
            <XCircle size={18} className="insurance-title-icon insurance-title-icon-excluded" />
            Not accepted
          </h3>
          <div className="insurance-grid insurance-grid-excluded" data-testid="insurance-excluded-grid">
            {notAccepted.map((insurance) => (
              <div
                key={`excluded-${insurance.name}`}
                className="insurance-card insurance-card-excluded"
                data-testid={`insurance-excluded-${insurance.name}`}
              >
                <XCircle className="insurance-x" size={20} />
                <InsurerLogo insurer={insurance} />
                <span className="insurance-name">{insurance.name}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
};

const Insurance = () => {
  return (
    <section className="insurance-section" data-testid="insurance-section">
      <div className="section-container">
        <div className="insurance-content">
          <div className="insurance-header">
            <div className="insurance-icon-wrapper">
              <Shield className="insurance-icon" size={48} />
            </div>
            <h2 className="section-title">Insurance & Payment</h2>
            <p className="section-subtitle">
              See which providers we work with — and which we don&apos;t — before you book
            </p>
          </div>

          <InsuranceGrids />

          <div className="insurance-note">
            <p>
              Don&apos;t see your insurance provider? <strong>Contact us</strong> — we can
              provide detailed invoices for most insurance claims. Self-pay
              options and block-package pricing available for out-of-network patients.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Insurance;
